// Utility functions related to workflows

// Import actual icon components
import {
  Archive,
  Bot,
  Clock,
  Linkedin,
  MessageCircle,
  MessagesSquare,
  Tag,
  Users,
  Workflow,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for end node IDs

// Mapping using actual components
const nodeStyleMapping = {
  trigger: {
    any_message: {
      label: 'Any Message',
      description: 'Trigger whenever any message is received',
      icon: MessagesSquare, // Use component
      bgClass: 'bg-green-500/10',
      borderClass: 'border-green-500/20',
      iconClass: 'text-green-500',
    },
    message_text: {
      label: 'Message Text',
      description: 'Trigger when a specific message is received',
      icon: MessagesSquare, // Use component
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    comment_text: {
      label: 'Comment Text',
      description: 'Trigger when a specific comment is received',
      icon: MessageCircle, // Use component
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    contact_tag: {
      label: 'Contact Tag',
      description: 'Trigger when a contact is tagged',
      icon: Tag, // Use component
      bgClass: 'bg-secondary/20',
      borderClass: 'border-secondary/30',
      iconClass: 'text-secondary-foreground',
    },
    schedule: {
      label: 'Schedule',
      description: 'Trigger based on time intervals and conditions',
      icon: Clock, // Use component
      bgClass: 'bg-orange-500/10',
      borderClass: 'border-orange-500/20',
      iconClass: 'text-orange-500',
    },
    linkedin_post_comment: {
      label: 'LinkedIn Post Comment',
      description: 'Trigger when a comment is received on a LinkedIn post',
      icon: Linkedin,
      bgClass: 'bg-blue-600/10',
      borderClass: 'border-blue-600/20',
      iconClass: 'text-blue-600',
    },
    // Add other trigger types here
  },
  action: {
    send_message: {
      label: 'Send Message',
      description: 'Send a message to the contact',
      icon: MessagesSquare, // Use component
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    reply_comment: {
      label: 'Reply to Comment',
      description: 'Reply to a comment on the post',
      icon: MessageCircle, // Use component
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    trigger_webhook: {
      label: 'Trigger Webhook',
      description: 'Send a webhook request to an external URL',
      icon: Workflow,
      bgClass: 'bg-secondary/20',
      borderClass: 'border-secondary/30',
      iconClass: 'text-secondary-foreground',
    },
    add_tag: {
      label: 'Add Contact Tag',
      description: 'Add one or more tags to the contact',
      icon: Tag, // Use component
      bgClass: 'bg-secondary/20',
      borderClass: 'border-secondary/30',
      iconClass: 'text-secondary-foreground',
    },
    contact_remove_tag: {
      label: 'Remove Contact Tag',
      description: 'Remove one or more tags from the contact',
      icon: Tag, // Use component
      bgClass: 'bg-destructive/10',
      borderClass: 'border-destructive/20',
      iconClass: 'text-destructive',
    },
    contact_state_change: {
      label: 'Change Contact State',
      description: 'Change the state of the contact',
      icon: Archive, // Use component
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    contact_change_owner: {
      label: 'Change Contact Owner',
      description: 'Change the owner of the contact',
      icon: Users, // Use component
      bgClass: 'bg-secondary/20',
      borderClass: 'border-secondary/30',
      iconClass: 'text-secondary-foreground',
    },
    delay: {
      label: 'Delay',
      description: 'Add a delay before the next action',
      icon: Clock, // Use component
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    ai_select_message: {
      label: 'AI Pick',
      description:
        'Revio AI will select the most appropriate message to send based on the conversation context',
      icon: Bot, // Use component
      bgClass: 'bg-purple-500/10',
      borderClass: 'border-purple-500/20',
      iconClass: 'text-purple-500',
    },
    linkedin_reply_comment: {
      label: 'LinkedIn Reply Comment',
      description: 'Reply to a comment on a LinkedIn post',
      icon: Linkedin,
      bgClass: 'bg-blue-600/10',
      borderClass: 'border-blue-600/20',
      iconClass: 'text-blue-600',
    },
    // Add other action types here
  },
  branch: {
    label: 'Branch',
    description: 'Add a conditional branch to your workflow',
    icon: Workflow, // Use component
    bgClass: 'bg-secondary/20',
    borderClass: 'border-secondary/30',
    iconClass: 'text-secondary-foreground',
  },
};

// Function to get style properties based on type and specific subtype (triggerType/actionType)
const getNodeStyleData = (nodeCategory, subType) => {
  const categoryMap = nodeStyleMapping[nodeCategory];
  if (!categoryMap) return {};
  return categoryMap[subType] || { label: subType, description: '' }; // Default fallback
};

// Helper function to convert stored workflow data (API format) back to React Flow format
export const convertWorkflowToFlowFormat = (workflow) => {
  if (!workflow || !workflow.triggers || !workflow.steps) {
    console.error('Invalid workflow data for conversion:', workflow);
    return { nodes: [], edges: [] };
  }

  const nodes = [];
  const edges = [];
  // No explicit positioning needed - set to { x: 0, y: 0 } and let layout engine work
  const initialPosition = { x: 0, y: 0 };

  // Map step IDs to the corresponding Node IDs for edge creation
  const stepIdToNodeIdMap = new Map();
  // Map branch IDs to their corresponding branch-data node IDs
  const branchIdToDataNodeIdMap = new Map(); // Added for branch reconstruction

  // 1. Process Trigger
  const triggerData = workflow.triggers[0]; // Assuming one trigger
  const triggerNodeId = 'start'; // Standard ID for the trigger node
  const triggerStyle = getNodeStyleData('trigger', triggerData.type);

  nodes.push({
    id: triggerNodeId,
    type: 'trigger',
    position: initialPosition,
    data: {
      label: triggerStyle.label || triggerData.type.replace('_', ' '),
      description: triggerStyle.description || 'Workflow starting point',
      icon: triggerStyle.icon,
      bgClass: triggerStyle.bgClass,
      borderClass: triggerStyle.borderClass,
      iconClass: triggerStyle.iconClass,
      // Core config data needed by TriggerConfiguration
      triggerType: triggerData.type,
      config: (() => {
        const triggerType = triggerData.type;
        const operator = triggerData.operator;
        const apiValue = triggerData.value;
        const config = { operator }; // Start with operator

        if (triggerType === 'contact_tag') {
          if (operator === 'equals') {
            // Expect string value
            config.tags = typeof apiValue === 'string' ? [apiValue] : [];
          } else if (operator === 'in') {
            // Expect array value
            config.tags = Array.isArray(apiValue) ? apiValue : [];
          }
          // tagObjects will be added later in WorkflowEditor
        } else if (triggerType === 'message_text') {
          if (operator === 'equals') {
            // Expect string value
            config.text = typeof apiValue === 'string' ? apiValue : '';
          } else if (operator === 'in') {
            // Expect array value
            config.texts = Array.isArray(apiValue) ? apiValue : [];
          } else if (operator === 'word_in') {
            // Expect array value for words
            config.words = Array.isArray(apiValue) ? apiValue : [];
          }
        } else if (triggerType === 'comment_text') {
          if (operator === 'equals') {
            // Expect string value
            config.text = typeof apiValue === 'string' ? apiValue : '';
          } else if (operator === 'in') {
            // Expect array value
            config.texts = Array.isArray(apiValue) ? apiValue : [];
          } else if (operator === 'word_in') {
            // Expect array value for words
            config.words = Array.isArray(apiValue) ? apiValue : [];
          }
        } else if (triggerType === 'any_message') {
          // Any message trigger doesn't need additional configuration
          config.value = null;
        } else if (triggerType === 'schedule') {
          // Handle schedule trigger configuration
          if (apiValue && typeof apiValue === 'object') {
            config.value = {
              unit: apiValue.unit || 'minutes',
              value: apiValue.value || 30,
            };
          }
          // Handle conditions if they exist
          if (triggerData.conditions && Array.isArray(triggerData.conditions)) {
            config.conditions = triggerData.conditions.map((condition) => {
              if (condition.field === 'contact_tag') {
                return {
                  ...condition,
                  value:
                    typeof condition.value === 'string'
                      ? [condition.value]
                      : condition.value,
                };
              }
              return condition;
            });
          }
        } else if (triggerType === 'linkedin_post_comment') {
          // Handle LinkedIn post comment trigger
          if (operator === 'equals') {
            config.text = typeof apiValue === 'string' ? apiValue : '';
          } else if (operator === 'in') {
            config.texts = Array.isArray(apiValue) ? apiValue : [];
          } else if (operator === 'word_in') {
            config.words = Array.isArray(apiValue) ? apiValue : [];
          }
          // Handle post_ids
          if (triggerData.post_ids && Array.isArray(triggerData.post_ids)) {
            config.post_ids = triggerData.post_ids;
          }
        }
        return config;
      })(), // Immediately invoke the function to set the config
    },
  });

  // 2. Process Steps into Nodes
  workflow.steps.forEach((step) => {
    const nodeId = step.id; // Use the step's ID as the node ID
    stepIdToNodeIdMap.set(step.id, nodeId);
    let nodeType = '';
    let nodeData = {};

    if (step.type === 'action' || step.type === 'delay') {
      nodeType = 'action'; // Both are represented by 'action' nodes in the flow
      const actionType =
        step.type === 'delay' ? 'delay' : step.actions?.[0]?.type;
      if (!actionType) {
        console.warn('Skipping step with missing action type:', step);
        return; // Skip malformed steps
      }
      const styleData = getNodeStyleData('action', actionType);
      let config = {};
      if (actionType === 'delay') {
        config = { value: step.delay, unit: step.delay_unit };
      } else if (step.actions && step.actions[0]) {
        // Reconstruct config based on how ActionConfiguration saves it
        switch (actionType) {
          case 'send_message':
            config = { message: step.actions[0].value };
            break;
          case 'reply_comment':
            config = { comment: step.actions[0].value };
            break;
          case 'contact_add_tag':
          case 'contact_remove_tag':
            // Assuming value is array of tag IDs, fetch tagObjects from store later if needed
            config = { tags: step.actions[0].value };
            break;
          case 'contact_state_change':
            config = { state: step.actions[0].value };
            break;
          case 'contact_change_owner': {
            const actionValue = step.actions[0].value;
            const operator = step.actions[0].operator;
            if (operator === 'weighted_allocation' && Array.isArray(actionValue)) {
              // Multiple owners with weighted allocation
              config = {
                ownerMode: 'multiple',
                owners: actionValue, // Array of { user_id, allocation }
              };
            } else {
              // Single owner (legacy format)
              config = {
                ownerMode: 'single',
                owner: actionValue,
              };
            }
            break;
          }
          case 'trigger_webhook': {
            const webhookValue = step.actions[0].value;
            config = {
              url: webhookValue.url || '',
              method: webhookValue.method || 'POST',
            };
            break;
          }
          case 'ai_select_message': {
            const aiValue = step.actions[0].value;
            config = {
              messages: aiValue.messages || [],
              custom_prompt: aiValue.custom_prompt || '',
            };
            break;
          }
          case 'linkedin_reply_comment':
            config = { comment: step.actions[0].value };
            break;
          default:
            config = step.actions[0].value; // Fallback
        }
      }
      nodeData = {
        ...styleData, // includes label, description, icon etc.
        actionType: actionType,
        config: config,
        // Add handleActionDelete later if needed/possible
      };
    } else if (step.type === 'branch') {
      nodeType = 'branch-config';
      const styleData = getNodeStyleData('branch', 'branch');
      // Map API branch data to the format expected by BranchConfigNode/Sheet
      const nodeBranches = step.branches.map((branch) => ({
        id: branch.id,
        name: branch.name, // Generate default name
        // Convert API condition/group structure back to array of groups expected by config component
        conditions: branch.condition ? [[branch.condition]] : [], // Wrap condition/group in nested array
        // Note: We don't store next_step_id here; that's handled by edges/branch-data nodes
      }));
      nodeData = {
        ...styleData, // Label, icon, etc.
        branches: nodeBranches,
        fallbackBranchId: step.default_branch_id, // Store the ID of the fallback branch
        // Inject handleDeleteBranchConfig later in the WorkflowEditor component
      };
      // --- Create intermediate branch-data nodes for each branch path ---
      step.branches.forEach((branch) => {
        const branchDataNodeId = `data-${branch.id}`; // Unique ID for the data node
        branchIdToDataNodeIdMap.set(branch.id, branchDataNodeId); // Map for edge creation
        nodes.push({
          id: branchDataNodeId,
          type: 'branch-data',
          position: initialPosition, // Layout engine will handle
          // Add name and conditions to allow BranchDataNode component to display them
          data: {
            branchId: branch.id,
            name: branch.id.includes('fallback') ? 'Fallback' : branch.name,
            conditions: branch.condition ? [[branch.condition]] : [], // Use same format as branch-config
          },
        });
      });
      // --- End branch-data node creation ---
    } else {
      console.warn('Unknown step type during conversion:', step.type);
      return; // Skip unknown types
    }

    nodes.push({
      id: nodeId,
      type: nodeType,
      position: initialPosition, // Let layout engine handle positioning
      data: nodeData,
    });
  });

  // 3. Create Edges based on connections
  // Edge from Trigger to the first step
  const firstStep = workflow.steps.find((step) => step.order === 1);
  if (firstStep && stepIdToNodeIdMap.has(firstStep.id)) {
    const firstNodeId = stepIdToNodeIdMap.get(firstStep.id);
    edges.push({
      id: `e-${triggerNodeId}-${firstNodeId}`,
      source: triggerNodeId,
      target: firstNodeId,
      type: 'custom',
      // Add edge data needed by CustomEdge/WorkflowBuilder (e.g., for add button)
      data: { showAddButton: true, allowBranchStep: true },
    });
  }

  // Edges between steps
  workflow.steps.forEach((step) => {
    const sourceNodeId = stepIdToNodeIdMap.get(step.id);
    if (!sourceNodeId) return; // Skip if source node wasn't created

    if (
      (step.type === 'action' || step.type === 'delay') &&
      step.next_step_id
    ) {
      // Simple action/delay connection
      const targetNodeId = stepIdToNodeIdMap.get(step.next_step_id);
      if (targetNodeId) {
        edges.push({
          id: `e-${sourceNodeId}-${targetNodeId}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'custom',
          data: { showAddButton: true, allowBranchStep: false }, // Allow adding after actions/delays, but not branches
        });
      } else {
        console.warn(
          `Target node for step ${step.next_step_id} not found for edge from ${sourceNodeId}`
        );
        // Optionally add edge to an implicit END node here if needed?
      }
    } else if (step.type === 'branch') {
      // --- Create edges from branch-config to branch-data nodes ---
      step.branches.forEach((branch) => {
        const branchDataNodeId = branchIdToDataNodeIdMap.get(branch.id);
        if (branchDataNodeId) {
          edges.push({
            id: `e-${sourceNodeId}-${branchDataNodeId}`, // Config -> Data edge ID
            source: sourceNodeId, // branch-config node ID
            target: branchDataNodeId, // branch-data node ID
            type: 'custom',
            data: { showAddButton: false }, // *** No add button here ***
          });

          // --- Create edges from branch-data nodes to next step or END ---
          if (branch.next_step_id) {
            const targetNodeId = stepIdToNodeIdMap.get(branch.next_step_id);
            if (targetNodeId) {
              edges.push({
                id: `e-${branchDataNodeId}-${targetNodeId}`, // Data -> Next edge ID
                source: branchDataNodeId, // branch-data node ID
                target: targetNodeId, // Next step node ID
                // No sourceHandle needed for branch-data node (assuming default output)
                type: 'custom',
                data: { showAddButton: true, allowBranchStep: true }, // Allow adding steps after branch path
              });
            } else {
              console.warn(
                `Target node for branch ${branch.id} (step ${branch.next_step_id}) not found for edge from ${branchDataNodeId}`
              );
              // Implicit END node logic below will handle this case
            }
          }
          // If branch.next_step_id is null/undefined, the implicit END node logic below handles it
        } else {
          console.warn(
            `Branch data node not found for branch ID ${branch.id} when creating edges.`
          );
        }
      });
      // --- End branch edge creation ---
    }
  });

  // --- 4. Add Implicit End Nodes and Edges ---
  const createdNodeIds = new Set(nodes.map((n) => n.id));
  const sourceNodeIdsWithEdges = new Set(edges.map((e) => e.source)); // Track all nodes that are sources in *any* edge
  // No longer need branchHandlesWithEdges - check sourceNodeIdsWithEdges instead

  nodes.forEach((node) => {
    // Skip trigger, branch-config, and already created end nodes
    // branch-config nodes are handled via their branch-data nodes now
    if (
      node.type === 'trigger' ||
      node.type === 'end' ||
      node.type === 'branch-config'
    ) {
      return;
    }

    // Check if the node (action, delay, or branch-data) is a source for any edge
    if (!sourceNodeIdsWithEdges.has(node.id)) {
      const endNodeId = `end-${node.id}-${uuidv4().substring(0, 4)}`; // Unique ID based on source
      if (!createdNodeIds.has(endNodeId)) {
        nodes.push({
          id: endNodeId,
          type: 'end',
          position: initialPosition, // Layout engine will handle
          data: { label: 'END' },
        });
        createdNodeIds.add(endNodeId); // Track added end node
      }
      edges.push({
        id: `e-${node.id}-${endNodeId}`,
        source: node.id,
        target: endNodeId,
        type: 'custom',
        // Determine if adding steps is allowed based on the source node type
        data: {
          showAddButton: true,
          allowBranchStep: node.type === 'branch-data', // Allow branches only after branch-data nodes
        },
      });
    }
  });
  // --- End Implicit End Node Logic ---

  return { nodes, edges };
};

export const validateWorkflow = (workflowSteps, tempTrigger, channels) => {
  const validationErrors = [];

  // Validate at least one trigger is selected
  if (!tempTrigger || tempTrigger.length === 0) {
    validationErrors.push('At least one trigger must be selected');
  }

  // Validate workflow steps
  if (!workflowSteps || workflowSteps.length === 0) {
    validationErrors.push('At least one step must be added to the workflow');
  } else {
    // Validate at least one action or delay type exists
    const hasAction = workflowSteps.some((step) => step.type === 'action');
    if (!hasAction) {
      validationErrors.push('Workflow must contain at least one action');
    }
  }

  // Validate at least one channel is selected
  if (!channels || channels.length === 0) {
    validationErrors.push('At least one channel must be selected');
  }

  return validationErrors;
};
